import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { CircleNotch, CalendarDots, Users, Clock } from "@phosphor-icons/react";
import { apiGet } from "@/lib/api-error";
import { formatCurrency, formatDate, formatTimeShort } from "@/lib/format";
import { ssrPage } from "@/ssr/define-page";
import { definePageMeta } from "@/ssr/define-meta";
import { buildPageStructuredData } from "@/ssr/structured-data";

interface PublicCycle {
  id: string;
  name: string;
  description: string;
  price: number;
  maxParticipants: number;
  durationMinutes: number;
  sessionCount: number;
  firstSessionAt: string | null;
  remainingSeats: number;
  therapist: {
    id: string;
    name: string;
    profileImageUrl: string | null;
  };
}

const groupTherapiesMeta = definePageMeta({
  titleKey: "groupTherapies.meta.title",
  descriptionKey: "groupTherapies.meta.description",
  ogImage: "/og-default.jpg",
  structuredData: (ctx) => buildPageStructuredData(ctx, {
    titleKey: "groupTherapies.meta.title",
    descriptionKey: "groupTherapies.meta.description",
    ogImage: "/og-default.jpg",
    pageType: "CollectionPage",
    breadcrumbs: [
      { name: "Psihotron", path: "/" },
      { name: ctx.t("groupTherapies.meta.breadcrumb"), path: "/grupne-terapije" },
    ],
  }),
});

export const Route = createFileRoute("/grupne-terapije/")(
  ssrPage({
    component: GroupTherapiesListing,
    meta: groupTherapiesMeta,
  }),
);

function GroupTherapiesListing() {
  const { data, isLoading } = useQuery({
    queryKey: ["public", "group-therapies"],
    queryFn: () =>
      apiGet<{ cycles: PublicCycle[] }>(
        "/api/group-therapies",
        "Greška pri učitavanju grupnih terapija"
      ),
  });

  const cycles = data?.cycles ?? [];

  return (
    <div className="min-h-screen">
      <section
        className="py-16 sm:py-20"
        style={{
          background: "linear-gradient(135deg, oklch(98% 0.005 84), oklch(95% 0.02 84))",
        }}
      >
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <h1
            className="text-3xl font-bold text-foreground sm:text-4xl lg:text-5xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Grupne terapije
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            Prijavite se za ciklus grupne terapije i radite na važnim temama uz stručno vodstvo i
            podršku grupe.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        {isLoading ? (
          <div className="flex h-48 items-center justify-center">
            <CircleNotch className="size-8 animate-spin text-primary" />
          </div>
        ) : cycles.length === 0 ? (
          <p className="text-center text-muted-foreground">
            Trenutno nema otvorenih grupnih terapija. Provjerite uskoro.
          </p>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {cycles.map((c) => (
              <article
                key={c.id}
                className="flex flex-col overflow-hidden rounded-xl border border-[#ece8df] bg-white shadow-sm transition hover:shadow-md"
              >
                <div className="flex flex-1 flex-col gap-3 p-5">
                  <div className="flex items-center gap-3">
                    {c.therapist.profileImageUrl ? (
                      <img
                        src={c.therapist.profileImageUrl}
                        alt=""
                        className="size-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="size-8 rounded-full bg-[#ece8df]" />
                    )}
                    <span className="text-sm text-muted-foreground">{c.therapist.name}</span>
                  </div>
                  <h3
                    className="text-lg font-semibold text-foreground"
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    {c.name}
                  </h3>
                  <p className="line-clamp-3 text-sm text-muted-foreground">{c.description}</p>
                  <div className="grid grid-cols-2 gap-y-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <CalendarDots className="size-4" />
                      {c.firstSessionAt
                        ? `${formatDate(c.firstSessionAt)} u ${formatTimeShort(c.firstSessionAt)}`
                        : "—"}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock className="size-4" />
                      {c.sessionCount} sesija · {c.durationMinutes} min
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Users className="size-4" />
                      {c.remainingSeats} slobodnih
                    </div>
                    <div className="font-medium text-foreground">{formatCurrency(c.price)}</div>
                  </div>
                  <div className="mt-auto pt-2">
                    <Link to="/grupne-terapije/$id" params={{ id: c.id }}>
                      <Button className="w-full">Detalji i prijava</Button>
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
